import type { UserClaims } from './useRBAC';
import { accessUnknownMessage, screenAccessUnknown } from './screenAccess';

/**
 * MAY THIS PERSON OPEN THE REPORTS PAGE — one answer, two readers (ADR-0035 A8).
 *
 * The nav item in `App.tsx` and the `/reports` route wrapper beside it both ask this function, so
 * the menu and the router cannot disagree. They disagreed once already in this program, with the
 * polarity flipped: a role check in the web and a permission gate on the server meant a grant
 * opened the API and left the link hidden.
 *
 * ⚠ THE OWNER ARM IS TRUE, NOT A FALLBACK HACK. `view:reports` is in
 * `OWNER_LOCKED_PERMISSIONS` (`backend/src/auth/permissions.service.ts`): the resolver re-adds it
 * for every OWNER and the edit endpoint refuses to revoke it, so an Owner ALWAYS holds it and this
 * arm can never admit somebody the server would refuse. The weld runs the other way too —
 * `backend/src/auth/permissions-docs.spec.ts` reads THIS FILE and fails the build if the OWNER arm
 * is here while the slug is not in that list, so deleting the lock without deleting the arm is a
 * red rather than a silent divergence.
 *
 * ⚠ WHY THE ARM EARNS ITS KEEP AT ALL, given the slug is always held. `render.yaml` deploys the
 * web and the API as SEPARATE services, so a web bundle routinely meets a backend that does not
 * know this slug: a deploy that lands web-first, a failed or rolled-back API deploy, or an offline
 * claims cache written against the older backend. In every one of those the permission read
 * answers a set with no `view:reports` in it, `hasPermission` is false, and without this arm the
 * clinic's OWNER is redirected off their own Reports page by their own browser. The server stays
 * the authority in both directions: the arm decides what to RENDER, never what to serve.
 */
export const REPORTS_PERMISSION = 'view:reports';

/** How the two refusal sentences name this screen ("…access to view Reports"). */
export const REPORTS_NOUN = 'Reports';

/**
 * ⚠ FALSE, AND THE ONLY `false` IN THE APP — the `showWhilePending` every `SCREEN_ACCESS` row sets
 * to `true` (see `ScreenAccessEntry`).
 *
 * The menu shows a gated item while the permission set is still pending, because a sidebar that
 * empties and re-fills on every cold load reads as a broken app. That arithmetic depends on almost
 * everybody holding the slug, and it inverts here: `view:reports` is owner-locked and delegated
 * deliberately, so for most of a clinic's staff the item appeared on every cold load and then
 * disappeared — a section being taken away, once per session, for people who never had it.
 *
 * It lives beside the rule rather than in the table because Reports is not IN the table (see the
 * table's own header); its answer belongs with the OWNER arm and the noun, in this file.
 */
export const REPORTS_SHOW_WHILE_PENDING = false;

export const canOpenReports = (
  claims: UserClaims | null,
  hasPermission: (permission: string) => boolean,
): boolean => claims?.role === 'OWNER' || hasPermission(REPORTS_PERMISSION);

/**
 * "WE DO NOT KNOW" — as opposed to "you may not", which is a different screen.
 *
 * `permissionsUnavailable` alone is NOT this question. A TRANSIENT failure keeps whatever set the
 * store already held for this uid (`mePermissions.publishPermissionsUnavailable`), so the flag can
 * be true while every answer is still usable — treating that as unknown would tell a person whose
 * permissions we can see perfectly well that we could not check them. The honest test is the flag
 * AND an empty set: every role's defaults are non-empty, so a person who holds nothing at all is a
 * read that never answered, never a real grant.
 *
 * It exists so the page can say so IN PLACE. A failed read must never become a redirect: bouncing
 * someone off `/reports` for a blip logs them out of a page they can open, and the app already
 * shows `PermissionsUnavailableBanner` for the condition itself.
 *
 * ⚠ THE PREDICATE MOVED, THE MEANING DID NOT. It is `screenAccessUnknown` in
 * `auth/screenAccess.ts` now, because the ten screens A1 gated ask the same question and two
 * spellings of "we could not check" is how they would answer it differently. This name stays as
 * the Reports-shaped alias its callers already read.
 */
export const reportsAccessUnknown = (
  claims: UserClaims | null,
  permissionsUnavailable: boolean,
): boolean => screenAccessUnknown(claims, permissionsUnavailable);

/** The one sentence the page shows for that state. Quoted verbatim by its tests. */
export const REPORTS_ACCESS_UNKNOWN_MESSAGE = accessUnknownMessage(REPORTS_NOUN);
